import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import { API_BASE_URL } from '../../../config/api';
import { documentService, vendorService, getErrorMessage } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';
import DocumentReadReview from './DocumentReadReview';

// Agreement PDFs (generated by Bull job, stored in S3) - show only in Agreements tab, not Documents tab
const AGREEMENT_FILENAMES = [
  'NDA_Agreement.pdf', 'SA_Agreement.pdf', '4M_Declaration.pdf',
  'Code_of_Conduct.pdf', 'Compliance_Agreement.pdf', 'Self_Declaration.pdf', 'Overseas_Agreement.pdf'
];

const DocumentsTab = ({ vendor, userRole, onVendorUpdated }) => {
  const toast = useToast();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [documentStats, setDocumentStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    expired: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // What the reader made of the document just uploaded, shown for the admin to confirm.
  const [pendingRead, setPendingRead] = useState(null);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [showRequestDocumentsModal, setShowRequestDocumentsModal] = useState(false);
  const [requestedDocTypes, setRequestedDocTypes] = useState([]);
  const [customDocs, setCustomDocs] = useState([]);
  const [customDocInput, setCustomDocInput] = useState('');
  const [isRequestingDocuments, setIsRequestingDocuments] = useState(false);
  const [regeneratingMsmeId, setRegeneratingMsmeId] = useState(null);

  // Document type mapping for categories
  const documentTypeCategories = {
    registration: ['pan_card', 'company_registration', 'incorporation_certificate', 'msme_certificate', 'msme_declaration'],
    financial: ['bank_statement', 'tax_certificate'],
    compliance: ['gst_certificate', 'business_license', 'insurance_certificate'],
    agreements: ['quality_certificate', 'other']
  };

  // Fetch documents, stats, and document requests
  useEffect(() => {
    if (vendor?.id) {
      fetchDocuments();
      fetchDocumentStats();
      fetchDocumentRequests();
    }
  }, [vendor?.id]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.getVendorDocuments(vendor.id);
      
      // Handle different response structures
      const documentsData = Array.isArray(response) ? response : (response?.data || response?.documents || []);
      setDocuments(documentsData);
    } catch (err) {
      console.error('Error fetching documents:', err);
      const errorMsg = getErrorMessage(err) || 'Failed to load documents. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/stats/vendor/${vendor.id}`, {
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle different response structures
      const statsData = data?.data || data;
      setDocumentStats({
        total: statsData?.total || 0,
        pending: statsData?.pending || 0,
        approved: statsData?.approved || 0,
        rejected: statsData?.rejected || 0,
        expired: statsData?.expired || 0,
      });
    } catch (err) {
      console.error('Error fetching document stats:', err);
      // Don't set error state for stats - it's not critical
    }
  };

  const fetchDocumentRequests = async () => {
    try {
      const response = await vendorService.getDocumentRequests(vendor.id);
      const data = response?.data ?? response;
      setDocumentRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching document requests:', err);
      setDocumentRequests([]);
    }
  };

  // Exclude agreement PDFs (they appear in Agreements tab only)
  const uploadedDocuments = documents.filter(
    doc => !(doc.document_type === 'OTHER' && AGREEMENT_FILENAMES.includes(doc.file_name))
  );

  // Calculate category counts
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') {
      return uploadedDocuments.length;
    }
    
    const categoryTypes = documentTypeCategories[categoryId] || [];
    return uploadedDocuments.filter(doc => categoryTypes.includes(doc.document_type)).length;
  };

  const documentCategories = [
    { id: 'all', label: 'All Documents', count: getCategoryCount('all') },
    { id: 'registration', label: 'Registration', count: getCategoryCount('registration') },
    { id: 'financial', label: 'Financial', count: getCategoryCount('financial') },
    { id: 'compliance', label: 'Compliance', count: getCategoryCount('compliance') },
    { id: 'agreements', label: 'Agreements', count: getCategoryCount('agreements') }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'rejected':
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return 'FileText';
    if (mimeType?.includes('image')) return 'Image';
    if (mimeType?.includes('document')) return 'FileText';
    return 'File';
  };

  // Human-friendly document type labels (handles both enum and lowercase)
  const formatDocumentType = (docType) => {
    if (!docType) return 'Document';
    const key = String(docType).toLowerCase();
    const map = {
      gst_certificate: 'GST Certificate',
      pan_card: 'PAN Card',
      bank_proof: 'Bank Proof',
      incorporation_certificate: 'Incorporation Certificate',
      digital_signature: 'Digital Signature',
      msme_certificate: 'MSME Certificate',
      bank_statement: 'Bank Statement',
      msme_declaration: 'MSME Declaration',
      company_registration: 'Company Registration',
      business_license: 'Business License',
      insurance_certificate: 'Insurance Certificate',
      quality_certificate: 'Quality Certificate',
      tax_certificate: 'Tax Certificate',
      other: 'Other Document'
    };
    if (map[key]) return map[key];
    // Fallback: titleize with spaces
    return String(docType)
      .split('_')
      .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Resubmission status for documents (matches ReviewPanel logic)
  const getDocumentResubmissionStatus = (doc) => {
    const now = new Date();
    const docMatchesRequest = (req) => {
      const requestedTypes = req.requested_types || [];
      const customDocsList = req.custom_docs || [];
      const docType = doc.document_type;
      if (docType !== 'OTHER') {
        const normalized = String(docType).toUpperCase();
        return requestedTypes.some((t) => String(t).toUpperCase() === normalized);
      }
      return customDocsList.some((c) => (c.name || '').trim().toLowerCase() === (doc.file_name || '').trim().toLowerCase());
    };
    const matchingRequest = documentRequests.find((req) => docMatchesRequest(req));
    if (!matchingRequest) return null;
    const expiresAt = new Date(matchingRequest.expires_at);
    if (matchingRequest.used) return 'submitted';
    if (expiresAt < now) return 'expired';
    return 'requested';
  };

  const DOCUMENT_TYPE_OPTIONS = [
    { value: 'INCORPORATION_CERTIFICATE', label: 'Company Incorporation' },
    { value: 'GST_CERTIFICATE', label: 'GST Certificate' },
    { value: 'PAN_CARD', label: 'PAN Card' },
    { value: 'BANK_PROOF', label: 'Bank Proof' },
    { value: 'DIGITAL_SIGNATURE', label: 'Digital Signature' },
    ...(vendor?.msme_status === 'MSME' || vendor?.msme_status === 'msme'
      ? [{ value: 'MSME_CERTIFICATE', label: 'Udyam Certificate' }]
      : []),
  ];

  const toggleDocType = (value) => {
    setRequestedDocTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const addCustomDoc = () => {
    const name = customDocInput.trim();
    if (name && !customDocs.some((c) => c.name === name)) {
      setCustomDocs((prev) => [...prev, { name }]);
      setCustomDocInput('');
    }
  };

  const removeCustomDoc = (name) => {
    setCustomDocs((prev) => prev.filter((c) => c.name !== name));
  };

  const handleRequestDocuments = async () => {
    if (requestedDocTypes.length === 0 && customDocs.length === 0) {
      toast.error('Please select at least one document type or add a custom document');
      return;
    }
    setIsRequestingDocuments(true);
    try {
      await vendorService.requestDocuments(vendor.id, {
        requested_types: requestedDocTypes,
        custom_docs: customDocs,
      });
      toast.success('Document request sent. Link emailed to vendor.');
      setShowRequestDocumentsModal(false);
      setRequestedDocTypes([]);
      setCustomDocs([]);
      setCustomDocInput('');
      await fetchDocumentRequests();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to send document request');
    } finally {
      setIsRequestingDocuments(false);
    }
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? uploadedDocuments 
    : uploadedDocuments.filter(doc => {
        const categoryTypes = documentTypeCategories[selectedCategory] || [];
        return categoryTypes.includes(doc.document_type);
      });

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  const handleUpload = async ({ file, documentType }) => {
    try {
      setUploading(true);
      const res = await documentService.uploadDocument(vendor.id, file, documentType);
      await fetchDocuments();
      await fetchDocumentStats();
      setUploadModalOpen(false);

      // The server reads it on the way in. If it found anything, show it for review rather than
      // making the admin go and look for it.
      const read = res?.data?.read || null;
      setPendingRead(read);
      if (read) {
        toast.success(`Uploaded — we read ${read.fields.length} value${read.fields.length === 1 ? '' : 's'} off it`);
      } else {
        toast.success('Document uploaded successfully!');
      }
    } catch (err) {
      toast.error(`Upload failed: ${getErrorMessage(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      // Try authenticated endpoint first
      try {
        const response = await documentService.downloadDocument(documentId);
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `document-${documentId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (authError) {
        // Fallback to public endpoint if authenticated fails
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/public/download`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `document-${documentId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error(`Download failed: ${err.message || 'Please try again.'}`);
    }
  };

  const handlePreview = async (documentId) => {
    try {
      // Try authenticated endpoint first
      let blob;
      try {
        const response = await documentService.downloadDocument(documentId);
        blob = response.data;
      } catch (authError) {
        // Fallback to public endpoint if authenticated fails
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/public/download?inline=true`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Preview failed: ${response.status}`);
        }

        blob = await response.blob();
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Error previewing document:', err);
      toast.error(`Preview failed: ${err.message || 'Please try again.'}`);
    }
  };

  const handleRegenerateMsmeDeclaration = async (docId) => {
    if (!vendor?.id) return;
    try {
      setRegeneratingMsmeId(docId);
      await vendorService.regenerateMsmeDeclaration(vendor.id);
      toast.success('MSME declaration has been regenerated.');
      await fetchDocuments();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to queue regeneration.');
    } finally {
      setRegeneratingMsmeId(null);
    }
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading documents...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="text-center py-12">
            <Icon name="AlertCircle" size={64} className="text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Documents</h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button variant="default" onClick={fetchDocuments}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Document Categories */}
      <div className="flex flex-wrap gap-2">
        {documentCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === category.id
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
            }`}
          >
            <span>{category.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              selectedCategory === category.id
                ? 'bg-white/20 text-white'
                : 'bg-slate-200 text-slate-600'
            }`}>
              {category.count}
            </span>
          </button>
        ))}
      </div>

      {/* Upload New Document */}
      {(userRole === 'Admin' || userRole === 'Approver') && (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Upload New Document</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Add new documents or update existing ones
                </p>
              </div>
            <Button 
              variant="default" 
              iconName="Upload" 
              iconPosition="left"
              onClick={() => setUploadModalOpen(true)}
            >
              Upload Document
            </Button>
          </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md border border-slate-200 shadow-sm bg-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Upload Document</h3>
            <UploadForm 
              onSubmit={handleUpload} 
              onCancel={() => setUploadModalOpen(false)}
              uploading={uploading}
            />
            </CardContent>
          </Card>
        </div>
      )}

      {/* What the reader made of the document just uploaded — reviewed and confirmed here. */}
      {pendingRead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-slate-200 bg-white shadow-sm">
            <CardContent className="p-6">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Review what we read</h3>
                  <p className="mt-0.5 text-sm text-slate-600">
                    The document is saved either way. Writing these to the vendor record is your call.
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPendingRead(null)} iconName="X" />
              </div>
              <DocumentReadReview
                read={pendingRead}
                onApplied={() => {
                  fetchDocuments();
                  if (onVendorUpdated) onVendorUpdated();
                }}
              />
              <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={() => setPendingRead(null)}>Done</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Request Documents - button above documents grid */}
      {(userRole === 'Admin' || userRole === 'Approver') && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRequestedDocTypes([]);
              setCustomDocs([]);
              setCustomDocInput('');
              setShowRequestDocumentsModal(true);
            }}
          >
            <Icon name="Send" size={14} className="mr-2" />
            Request Documents
          </Button>
        </div>
      )}

      {/* Documents Grid */}
      {filteredDocuments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredDocuments.map((document) => {
            const resubmissionStatus = getDocumentResubmissionStatus(document);
            return (
            <Card key={document.id} className="border border-slate-200 shadow-sm bg-white">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Icon name={getFileIcon(document.mime_type)} size={24} className="text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-slate-900 truncate max-w-full">
                          {formatDocumentType(document.document_type)}
                        </h4>
                        {resubmissionStatus && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              resubmissionStatus === 'requested'
                                ? 'bg-amber-100 text-amber-800'
                                : resubmissionStatus === 'submitted'
                                ? 'bg-green-100 text-green-800'
                                : resubmissionStatus === 'expired'
                                ? 'bg-slate-200 text-slate-600'
                                : ''
                            }`}
                          >
                            {resubmissionStatus === 'requested' && 'Resubmission requested'}
                            {resubmissionStatus === 'submitted' && 'Resubmitted'}
                            {resubmissionStatus === 'expired' && 'Request expired'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">
                        {document.mime_type?.split('/')[1]?.toUpperCase()} • {formatFileSize(document.file_size)}
                      </p>
                    </div>
                  </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor('approved')}`}>
                    Approved
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Type:</span>
                  <span className="text-slate-900">{formatDocumentType(document.document_type)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Uploaded:</span>
                  <span className="text-slate-900">{formatDate(document.created_at)}</span>
                </div>
                {document.expiry_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Expires:</span>
                    <span className={`font-medium ${
                      isExpired(document.expiry_date) ? 'text-red-700' :
                      isExpiringSoon(document.expiry_date) ? 'text-amber-700' : 'text-slate-900'
                    }`}>
                      {formatDate(document.expiry_date)}
                      {isExpiringSoon(document.expiry_date) && !isExpired(document.expiry_date) && (
                        <span className="ml-1 text-xs">(Soon)</span>
                      )}
                      {isExpired(document.expiry_date) && (
                        <span className="ml-1 text-xs">(Expired)</span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  className="flex-1 min-w-[100px]"
                  onClick={() => handlePreview(document.id)}
                >
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  className="flex-1 min-w-[100px]"
                  onClick={() => handleDownload(document.id, document.file_name)}
                >
                  Download
                </Button>
                {(userRole === 'Admin' || userRole === 'Approver') &&
                  String(document.document_type).toUpperCase() === 'MSME_DECLARATION' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-w-[100px]"
                      disabled={regeneratingMsmeId === document.id}
                      onClick={() => handleRegenerateMsmeDeclaration(document.id)}
                    >
                      Regenerate
                    </Button>
                  )}
              </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      ) : (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Icon name="FileText" size={64} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Documents Available</h3>
              <p className="text-slate-600 mb-6">
            {selectedCategory === 'all' 
              ? "This vendor hasn't uploaded any documents yet."
              : `No documents found in the ${selectedCategory} category.`
            }
          </p>
          {(userRole === 'Admin' || userRole === 'Approver') && (
            <Button 
              variant="default" 
              iconName="Upload" 
              iconPosition="left"
              onClick={() => setUploadModalOpen(true)}
            >
              Upload First Document
            </Button>
          )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Request Documents Modal */}
      {showRequestDocumentsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-slate-900">Request Documents</h3>
              <p className="text-sm text-slate-600 mt-1">Select documents to request from the vendor. A link will be emailed to them.</p>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Document types</label>
                <div className="space-y-2">
                  {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={requestedDocTypes.includes(opt.value)}
                        onCheckedChange={() => toggleDocType(opt.value)}
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Custom document</label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="e.g. Trade License"
                    value={customDocInput}
                    onChange={(e) => setCustomDocInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomDoc())}
                  />
                  <Button variant="outline" size="sm" onClick={addCustomDoc}>
                    Add
                  </Button>
                </div>
                {customDocs.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {customDocs.map((c) => (
                      <li key={c.name} className="flex items-center justify-between text-sm bg-slate-50 px-2 py-1 rounded">
                        <span>{c.name}</span>
                        <button
                          type="button"
                          onClick={() => removeCustomDoc(c.name)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowRequestDocumentsModal(false)}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleRequestDocuments}
                disabled={isRequestingDocuments || (requestedDocTypes.length === 0 && customDocs.length === 0)}
                loading={isRequestingDocuments}
              >
                Send Request
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Document Summary */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={20} className="text-slate-600" />
            Document Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">{documentStats.total}</div>
              <div className="text-sm text-slate-600">Total Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-emerald-700 mb-1">{documentStats.approved}</div>
              <div className="text-sm text-slate-600">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-amber-700 mb-1">{documentStats.pending}</div>
              <div className="text-sm text-slate-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-700 mb-1">{documentStats.rejected}</div>
              <div className="text-sm text-slate-600">Rejected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-700 mb-1">{documentStats.expired}</div>
              <div className="text-sm text-slate-600">Expired</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Upload Form Component
const UploadForm = ({ onSubmit, onCancel, uploading }) => {
  const [formData, setFormData] = useState({
    document_type: '',
    file: null,
    expiry_date: ''
  });

  // Exactly the DocumentType values the database holds — anything else is rejected by Prisma.
  // The ticked ones are read on upload and can fill the vendor record.
  const documentTypes = [
    { value: 'GST_CERTIFICATE', label: 'GST Certificate', read: true },
    { value: 'PAN_CARD', label: 'PAN Card', read: true },
    { value: 'BANK_PROOF', label: 'Bank Proof / Cancelled Cheque', read: true },
    { value: 'MSME_CERTIFICATE', label: 'MSME (Udyam) Certificate', read: true },
    { value: 'INCORPORATION_CERTIFICATE', label: 'Certificate of Incorporation', read: true },
    { value: 'MSME_DECLARATION', label: 'MSME Declaration' },
    { value: 'DIGITAL_SIGNATURE', label: 'Digital Signature' },
    { value: 'OTHER', label: 'Other' }
  ];

  const willRead = documentTypes.find((t) => t.value === formData.document_type)?.read;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.document_type || !formData.file) {
      toast.warning('Please select document type and file');
      return;
    }

    onSubmit({ file: formData.file, documentType: formData.document_type, expiryDate: formData.expiry_date || null });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-900 mb-2">
          Document Type *
        </label>
        <select
          value={formData.document_type}
          onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
          className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-500"
          required
        >
          <option value="">Select document type</option>
          {documentTypes.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {willRead && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600">
            <Icon name="ScanText" size={12} className="text-primary" />
            We will read this document and show you what it says about the vendor.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          File *
        </label>
        <input
          type="file"
          onChange={(e) => setFormData(prev => ({ ...prev, file: e.target.files[0] }))}
          className="w-full p-2 border border-border rounded-md bg-background text-foreground"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          required
        />
        <p className="text-xs text-slate-600 mt-1">
          Allowed: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-900 mb-2">
          Expiry Date (Optional)
        </label>
        <input
          type="date"
          value={formData.expiry_date}
          onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
          className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-500"
        />
      </div>

      <div className="flex space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          className="flex-1"
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </form>
  );
};

export default DocumentsTab;